import React, { useState, useEffect, useCallback } from 'react';
import { IAssessment, IAnswer, IUnansweredQuestion } from 'models/EmployeeWorkshopAssessment';
import { Feature } from "models/api";
import { QueryClient, useMutation } from 'react-query';
import { getAssessmentQuestions } from 'framework/workshopAssessments';
import { Link, useParams } from 'react-router-dom';
import { useAlert } from 'contexts/alertContext';
import { handleError } from 'helpers/utils';
import { addEmployeeAnswer, getEmployeeWorkshopAssessment, updateElapsedTime, updateAssessmentStatus } from 'framework/employeeWorkshopAssessment';
import { IEmployeeWorkshopAssessment } from 'models/employeeWorkshopAssessmentDetails';
import { Box, Button, Typography, RadioGroup, FormControlLabel, Radio, List, ListItem, Skeleton, Stack } from '@mui/material';
import useApp from 'hooks/useApp';
import { Pages } from 'constants/enums';

const formatTime = (seconds: number) => {
  const clampedSeconds = Math.max(0, Math.floor(seconds));
  const minutes = Math.floor(clampedSeconds / 60);
  const remainingSeconds = clampedSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
};

interface Props {
  feature: Feature;
}

const EmployeeWorkshopAssessment: React.FC<Props> = ({ feature }) => {
  const [assessment, setAssessment] = useState<IAssessment | null>(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<IAnswer[]>([]);
  const [unansweredQuestions, setUnansweredQuestions] = useState<IUnansweredQuestion[]>([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [dataFetched, setDataFetched] = useState(false);
  const [employeeWorkshopAssessment, setEmployeeWorkshopAssessment] = useState<IEmployeeWorkshopAssessment>({} as IEmployeeWorkshopAssessment);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);
  const { push } = useApp()
  const [loading, setLoading] = useState<boolean>(true);
  const [loading1, setLoading1] = useState<boolean>(true);
  const queryClient = new QueryClient();
  const urlParams = useParams();
  const { setAlert } = useAlert();
  let id: string = urlParams[`${feature.singleName}Id`] as string;

  const addAnswer = useMutation({
    mutationFn: (data: {
      answer: IAnswer,
    }) => {
      return addEmployeeAnswer(data.answer, id);
    },
  });

  const updatedTime = useMutation({
    mutationFn: () => {
      return updateElapsedTime(id);
    },
  });

  const finishedAssessment = useMutation({
    mutationFn: () => {
      return updateAssessmentStatus(id);
    },
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response: any = await queryClient.fetchQuery(
        "get Workshop Assessment Details",
        () => getEmployeeWorkshopAssessment('/' + id)
      );
      setEmployeeWorkshopAssessment(response);
      if (!response.completed) {
        fetchQuestions();
      }
      if (response) {
        setAnswers(response.employeeWorkshopAssessmentAnswers || []);
        setElapsedTime(response.elapsedTime || 0);
      }
      setLoading(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading(false);
    }
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchQuestions = useCallback(async () => {
    setLoading1(true);
    try {
      const response: any = await queryClient.fetchQuery(
        "get Workshop Assessment",
        () => getAssessmentQuestions("/getquestions?workshopAssessmentId=" + id)
      );
      setAssessment(response.assessmentDefinition);
      setDataFetched(true);
      setLoading1(false);
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Something went wrong",
        type: "error",
      });
      setLoading1(false);
    }
  }, [queryClient, setAlert]);

  useEffect(() => {
    fetchQuestions();
  }, []);

  useEffect(() => {
    if (!dataFetched || assessmentCompleted) return;

    const incrementTimer = setInterval(() => {
      setElapsedTime((prev) => prev + 1);
    }, 1000);

    // Cleanup increment timer on unmount or dependency change
    return () => clearInterval(incrementTimer);
  }, [dataFetched, assessmentCompleted]);

  useEffect(() => {
    if (!dataFetched || assessmentCompleted) return;

    const totalTime = employeeWorkshopAssessment?.workshopAssessment?.timeToComplete ?? 0;
    if (totalTime > 0 && elapsedTime >= totalTime) {
      finishedAssessment.mutate();
      setAssessmentCompleted(true);
      setEmployeeWorkshopAssessment((prev) => ({
        ...prev,
        completed: true,
      }));
    }
  }, [elapsedTime, dataFetched, assessmentCompleted, employeeWorkshopAssessment, finishedAssessment]);

  useEffect(() => {
    if (!dataFetched || assessmentCompleted) return;

    const apiTimer = setInterval(async () => {
      await updatedTime.mutateAsync();
    }, 5000);

    // Cleanup apiTimer on unmount or dependency change
    return () => clearInterval(apiTimer);
  }, [dataFetched, assessmentCompleted]);


  if (loading || loading1) return <Stack spacing={2} p={2}>
    {Array.from({ length: 10 }).map((_, i) => (
      <Skeleton variant="rectangular" key={i} width="100%" height={30} />
    ))}
  </Stack>;
  if (!assessment) return null;

  const goToQuestion = (index: number) => {
    if (index < 0 || index >= assessment.vendorAssessmentQuestions.length) return;

    const currentQuestionId = assessment.vendorAssessmentQuestions[currentQuestionIndex].id;
    const answered = answers.some(answer => answer.questionId === currentQuestionId);

    setUnansweredQuestions((prevUnanswered) => {
      if (!answered) {
        return prevUnanswered.some(q => q.questionId === currentQuestionId)
          ? prevUnanswered
          : [...prevUnanswered, { questionId: currentQuestionId, questionOrder: currentQuestionIndex + 1 }];
      } else {
        return prevUnanswered.filter(q => q.questionId !== currentQuestionId);
      }
    });

    setCurrentQuestionIndex(index);
    fetchData();
  };

  const saveAnswer = async (questionId: string, optionId: string) => {
    setAnswers((prevAnswers): IAnswer[] => {
      const updatedAnswers = prevAnswers.filter(answer => answer.questionId !== questionId);
      return [...updatedAnswers, { questionId, selectedOptionId: optionId } as IAnswer];
    });
    const answer: IAnswer = {
      questionId: questionId,
      selectedOptionId: optionId,
      answered: true
    }
    await addAnswer.mutateAsync({ answer: answer });
    setUnansweredQuestions((prevUnanswered) => prevUnanswered.filter(q => q.questionId !== questionId));
  };


  const finishAssessment = async () => {
    if (unansweredQuestions.length > 0) {
      const confirmFinish = window.confirm("There are unanswered questions. Are you sure you want to finish?");
      if (!confirmFinish) return;
    }
    try {
      const response: any = await finishedAssessment.mutateAsync();
      employeeWorkshopAssessment.completed = true;
      
      // Check if we need to redirect to result page for psychometric assessments
      if (response?.redirectToResult && response?.assessmentDefinitionId) {
        // Redirect to the assessment result page
        push(`/employeeWorkshopAssessment/result/${response.assessmentDefinitionId}`);
      }
       else {
        // Default redirect to evaluation details
        (assessment?.type?.isPosttest ?? assessment?.isPosttest) && push('/employeeWorkshopEvaluationDetails/'+ employeeWorkshopAssessment.workshopAssessment.workshopId);
      }
    } catch (error: any) {
      setAlert({
        open: true,
        message: handleError(error) || "Failed to finish assessment",
        type: "error",
      });
    }
  };

  const currentQuestion = assessment.vendorAssessmentQuestions[currentQuestionIndex];
  const selectedAnswer = answers.find(answer => answer.questionId === currentQuestion.id)?.selectedOptionId;

  return (
    <>
    {employeeWorkshopAssessment.completed ? (
        <div>
          <h2>You have finished your assessment.</h2>
        </div>
      ) : (
        <Box sx={{ padding: 3, border: '1px solid #ddd', borderRadius: 2 }}>
          <Typography variant="h4" gutterBottom>
            {assessment.title}
          </Typography>

          <Typography variant="body1" gutterBottom>
            <strong>Time Remaining:</strong> {formatTime(Math.max((employeeWorkshopAssessment?.workshopAssessment?.timeToComplete ?? 0) - elapsedTime, 0))}
          </Typography>

          <Box sx={{ marginBottom: 3 }}>
            <Typography variant="h5" gutterBottom>
              {currentQuestion.questionOrder}. {currentQuestion.title}
            </Typography>

            <RadioGroup
              value={selectedAnswer}
              onChange={(e) => saveAnswer(currentQuestion.id, e.target.value)}
            >
              {currentQuestion.vendorAssessmentQuestionOptions.map((option) => (
                <FormControlLabel
                  key={option.id}
                  value={option.id}
                  control={<Radio />}
                  label={option.description}
                />
              ))}
            </RadioGroup>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, marginBottom: 3 }}>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => goToQuestion(currentQuestionIndex - 1)}
              disabled={currentQuestionIndex === 0}
            >
              Previous
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{ borderRadius: 0 }}
              onClick={() => goToQuestion(currentQuestionIndex + 1)}
              disabled={currentQuestionIndex === unansweredQuestions.length - 1}
            >
              Next
            </Button>
            <Button variant="contained" color="success" onClick={finishAssessment}>
              Finish
            </Button>
          </Box>

          <Box>
            <Typography variant="h6" gutterBottom>
              Unanswered Questions:
            </Typography>
            <List>
              {unansweredQuestions.map((q) => (
                <ListItem
                  key={q.questionId}
                  button
                  onClick={() => goToQuestion(q.questionOrder - 1)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  Question {q.questionOrder}
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>
      )}
    </>
  );
};

export default EmployeeWorkshopAssessment;
